import { useWebApp } from "@/features/webapp"
import { cn } from "@/lib/utils"

import overlayDefault from "@/assets/img/today/nav/overlay_default.webp"
import overlaySelected from "@/assets/img/today/nav/overlay_selected.webp"
import overlayNew from "@/assets/img/today/nav/overlay_new.webp"
import overlayNewSelected from "@/assets/img/today/nav/overlay_new_selected.webp"

import { todayCategories, type TodayCategory, type TodayCategoryItem } from "../config/categories"
import { useHorizontalWheelScroll } from "../hooks/use-horizontal-wheel-scroll"

interface TodayNavigationProps {
  selected: TodayCategory
  onSelect: (id: TodayCategory) => void
}

/** Horizontal category tab bar for the Today hub. Ported from TodayNavigation. */
export function TodayNavigation({ selected, onSelect }: TodayNavigationProps) {
  const categoriesContainerRef = useHorizontalWheelScroll<HTMLDivElement>()

  const handleClick = (id: TodayCategory) => {
    onSelect(id)
    document
      .getElementById(`today-cat-${id}`)
      ?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" })
  }

  return (
    <div
      ref={categoriesContainerRef}
      className="flex w-full select-none items-center gap-4 overflow-auto border-b border-purple-light/10 px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {todayCategories.map((category) => (
        <CategoryItem
          key={category.id}
          category={category}
          selected={selected === category.id}
          onClick={() => handleClick(category.id)}
        />
      ))}
    </div>
  )
}

interface CategoryItemProps {
  category: TodayCategoryItem
  selected: boolean
  onClick: () => void
}

function CategoryItem({ category, selected, onClick }: CategoryItemProps) {
  const { hasNewMarkers } = useWebApp()
  const isNew = Boolean(hasNewMarkers?.[category.id])

  const overlay = isNew
    ? selected
      ? overlayNewSelected
      : overlayNew
    : selected
      ? overlaySelected
      : overlayDefault

  return (
    <button
      type="button"
      id={`today-cat-${category.id}`}
      onClick={onClick}
      className={cn(
        "w-[72px] min-w-[72px] cursor-pointer border-b-2",
        selected ? "border-main-purple" : "border-transparent",
      )}
    >
      <div className="relative">
        <div className="flex size-[72px] items-end overflow-hidden rounded-full">
          <img src={category.image} alt={category.name} className="h-20 w-[72px] max-w-[72px]" />
        </div>
        <img src={overlay} alt="" className="absolute inset-0 size-full object-cover" />
      </div>
      <div
        className={cn(
          "mb-1.5 mt-1 text-center font-body text-xs font-medium",
          selected ? "text-purple-light" : "text-purple-light/40",
        )}
      >
        {category.name}
      </div>
    </button>
  )
}
